import pako from 'pako';

import { decodeBase64ToBytes, encodeBytesToBase64 } from './base64Codec';

/**
 * Represents optional guest, discount, and holder data to be encoded.
 * https://docs.liteapi.travel/docs/deeplinking-to-whitelabel#supported-xdata-fields
 */
export interface XDataInput {
  /**
   * Optional list of guests.
   */
  guests?: Array<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>;

  /**
   * Optional discount code.
   */
  discount?: {
    code?: string;
  };

  /**
   * Optional booking holder information.
   */
  holder?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Compresses and encodes json data into a Base64-encoded gzipped string (`xData`).
 * This format is typically used for lightweight, URL-safe data transfer.
 *
 * https://docs.liteapi.travel/docs/deeplinking-to-whitelabel#supported-xdata-fields
 *
 * @param data - The input data object to encode.
 * @returns A Base64 string representing the compressed data.
 *
 */
export const encodeData = <T>(data?: T): string | undefined => {
  if (!data) return;
  const json = JSON.stringify(data);
  const compressed = pako.gzip(json);
  const base64 = encodeBytesToBase64(compressed);
  return base64;
};

/**
 * Decodes a Base64-encoded gzipped string (`xData`) into an object.
 *
 * @param encodedData - The Base64-encoded gzipped string to decode.
 * @returns The decoded object or undefined if the decoding fails.
 */
export const decodeData = <T>(encodedData: string): T | undefined => {
  const compressed = decodeBase64ToBytes(encodedData);
  const json = pako.ungzip(compressed, { to: 'string' });
  return JSON.parse(json) as T;
};
